import { EventNames } from '@farmasiet/frontendcore-reactcomponents';

import { deleteFor, postFor, putFor } from '../../../ReactComponents/API';
import { findWithIndex } from '../../../ReactComponents/FindWithIndex';
import { GoogleTagManager } from '../../../Tracking/GoogleTagManager/GoogleTagManager';
import { ICartDetails } from '../../Cart/ICartDetails.csharp';
import { CartItemValidationIssueViewModel } from '../../Cart/Item/ViewModels/CartItemValidationIssueViewModel.csharp';
import { CartItemViewModel } from '../../Cart/Item/ViewModels/CartItemViewModel.csharp';
import { CheckoutApiRoutes } from '../CheckoutApiRoutes.csharp';
import { handleCheckoutError } from '../Components/CheckoutBaseApi';
import { containsQuantityWarnings } from '../Validation/CheckoutValidator';
import { CheckoutViewModel } from '../ViewModel/CheckoutViewModel.csharp';
import { CheckoutUpdateLineItemResult } from './CheckoutUpdateLineItemResult.csharp';
import { RemoveLineItemsRequest } from './RemoveLineItemsRequest.csharp';

export interface ILineItemsApi {
    setQuantity: (productCode: string, quantity: number) => Promise<SetQuantityResult>;
    removeFromCart: (productCode: string) => Promise<boolean>;
    removeLineItems: (productCodes: string[]) => Promise<boolean>;
}

export class LineItemsApi implements ILineItemsApi {
    private routes: CheckoutApiRoutes;
    private getCart: () => ICartDetails;
    private updateCart: (cart: ICartDetails) => void;
    private setIsLoaderActive: (isLoaderActive: boolean) => void;

    constructor(
        routes: CheckoutApiRoutes,
        getCart: () => ICartDetails,
        updateCart: (cart: ICartDetails) => void,
        setIsLoaderActive: (isLoaderActive: boolean) => void
    ) {
        this.routes = routes;
        this.getCart = getCart;
        this.updateCart = updateCart;
        this.setIsLoaderActive = setIsLoaderActive;
    }

    private get cart() {
        return this.getCart();
    }

    public setQuantity = async (productCode: string, quantity: number): Promise<SetQuantityResult> => {
        const product = this.cart.summary.items.find((x) => x.code === productCode);
        if (!product) return SetQuantityResult.failed();

        const quantityBefore = product.quantityOrdered;
        if (quantity <= 0) return SetQuantityResult.failed();

        // eslint-disable-next-line
        const url = `${this.routes.cartItemUrl}?productCode=${productCode}&quantity=${quantity}&cartId=${this.cart.cartId}&loginExpected=${this.cart.isUserLoggedIn}&hash=${this.cart.hash}`;

        return await putFor<CheckoutUpdateLineItemResult>(url)
            .then((result) => {
                this.updateCart(result.checkout);

                if (result.validationIssues?.length > 0) {
                    this.setValidationIssuesToLineItem(this.cart.summary.items, productCode, result.validationIssues);
                }

                const updateResult = this.updateSucceeded(productCode, result.checkout);
                if (updateResult.succeeded) {
                    GoogleTagManager.changeQuantity(product, updateResult.quantity ?? quantity, quantityBefore);
                    this.dispatchQuantityChangeEvent(result.checkout);
                    return SetQuantityResult.succeeded(updateResult.quantity ?? quantity);
                }

                return SetQuantityResult.succeeded(updateResult.quantity ?? product.quantityOrdered);
            })
            .catch(async (error) => {
                const checkout = handleCheckoutError(error, this.cart);
                this.updateCart(checkout);
                return SetQuantityResult.failed();
            });
    };

    public removeFromCart = async (productCode: string): Promise<boolean> => {
        // eslint-disable-next-line
        const url = `${this.routes.cartItemUrl}?productCode=${productCode}&cartId=${this.cart.cartId}&loginExpected=${this.cart.isUserLoggedIn}&hash=${this.cart.hash}`;
        this.setIsLoaderActive(true);

        return await deleteFor<ICartDetails>(url)
            .then((cart) => {
                this.updateCart(cart);
                if (this.removeSucceeded(productCode, cart)) {
                    this.dispatchQuantityChangeEvent(cart);
                    return true;
                }

                return false;
            })
            .catch((error) => {
                const checkout = handleCheckoutError(error, this.cart);
                this.updateCart(checkout);
                return false;
            })
            .finally(() => {
                this.setIsLoaderActive(false);
            });
    };

    public removeLineItems = async (productCodes: string[]): Promise<boolean> => {
        const url = `${this.routes.removeLineItemsUrl}?hash=${this.cart.hash}`;
        this.setIsLoaderActive(true);

        const request: RemoveLineItemsRequest = { productCodes: productCodes };

        return await postFor<CheckoutViewModel>(url, request)
            .then((cart) => {
                this.updateCart(cart);
                if (this.itemsSuccessfullyRemoved(productCodes, cart)) {
                    this.dispatchQuantityChangeEvent(cart);
                    return true;
                }

                return false;
            })
            .catch((error) => {
                const checkout = handleCheckoutError(error, this.cart);
                this.updateCart(checkout);
                return false;
            })
            .finally(() => {
                this.setIsLoaderActive(false);
            });
    };

    private setValidationIssuesToLineItem = (
        items: CartItemViewModel[],
        productCode: string,
        issues: CartItemValidationIssueViewModel[]
    ): CartItemViewModel[] => {
        const { index, item } = findWithIndex(items, (x) => x.code == productCode);

        if (!item) {
            return items;
        }

        const codes = item.validationIssues.map((x) => x.code);
        for (const issue of issues) {
            if (codes.includes(issue.code) === false) {
                item.validationIssues.push(issue);
            }
        }

        items[index] = item;

        return items;
    };

    private dispatchQuantityChangeEvent = (cart: ICartDetails) => {
        const event = new CustomEvent(EventNames.CartTotalQuantityChanged, {
            detail: { totalQuantity: cart.summary.totalQuantity }
        });
        window.dispatchEvent(event);
    };

    private updateSucceeded = (productCode: string, cart: ICartDetails): { succeeded: boolean; quantity?: number } => {
        if (!cart?.summary?.items) return { succeeded: false, quantity: undefined };

        const updatedItem = cart.summary.items.find((x) => x.code === productCode);

        return {
            succeeded: (updatedItem && !containsQuantityWarnings(updatedItem)) ?? false,
            quantity: updatedItem && updatedItem.quantityOrdered
        };
    };

    private removeSucceeded = (productCode: string, cart: ICartDetails) => {
        if (!cart?.summary?.items) return false;

        const removedItem = cart.summary.items.find((x) => x.code === productCode);

        return !removedItem;
    };

    private itemsSuccessfullyRemoved = (productCodes: string[], cart: ICartDetails) => {
        if (!cart?.summary?.items) return false;

        return !cart.summary.items.some((i) => productCodes.includes(i.code));
    };
}

export class SetQuantityResult {
    quantity: number;
    succeeded: boolean;

    constructor(succeeded: boolean, quantity: number) {
        this.quantity = quantity;
        this.succeeded = succeeded;
    }

    static failed = () => new SetQuantityResult(false, 0);
    static succeeded = (quantity: number) => new SetQuantityResult(true, quantity);
}
